"use client"

import * as React from "react"
import { CheckIcon, CircleAlertIcon, CopyIcon, LayoutDashboardIcon, RefreshCcwIcon } from "lucide-react"

import { Button } from "./button"

/**
 * The **single** canonical full-screen error UI for every IbadaCloud app
 * (tenant + admin, both the React error boundary and the react-router
 * `errorElement`). Consumers pass localised labels and a recovery
 * pathname — they do not implement the visual layout, the copy button,
 * the technical-details disclosure, or the stack-trace gate.
 *
 * Do not fork this. If you need a new presentation, extend this file
 * and pick the variant via a prop.
 */

export type AppErrorScreenLabels = {
  /** Small uppercase eyebrow above the title. */
  eyebrow: string
  /** Headline (use a different one for stale-chunk recovery). */
  title: string
  /** One- or two-line explanation under the headline. */
  description: string
  /** Primary action label (typically "Reload page"). */
  reloadLabel: string
  /** Secondary action label (typically "Go to dashboard"). */
  homeLabel: string
  /** Summary of the disclosure that reveals route + stack. */
  detailsLabel: string
  /** Label for the route field. */
  routeLabel: string
  /** Label for the message field inside the details disclosure. */
  messageLabel: string
  /** Tooltip / aria-label for the copy button. */
  copyLabel: string
  /** Tooltip / aria-label when the copy succeeded (~2s window). */
  copiedLabel: string
}

export type AppErrorScreenProps = {
  error: Error | null
  /** Current pathname — caller passes `useLocation().pathname` or `window.location.pathname`. */
  pathname: string
  labels: AppErrorScreenLabels
  /** When true, the stack trace is rendered inside the details disclosure (dev-only by convention). */
  showStackTrace: boolean
  /** When true, route + technical details are visible to the user. Defaults to `showStackTrace`. */
  showDiagnostics?: boolean
  onReload: () => void
  onHome: () => void
  /** Hook the consumer to record copy success (toast.success / analytics). */
  onCopySuccess?: () => void
  /** Hook the consumer to record copy failure (toast.error). */
  onCopyError?: () => void
}

export function AppErrorScreen({
  error,
  pathname,
  labels,
  showStackTrace,
  showDiagnostics,
  onReload,
  onHome,
  onCopySuccess,
  onCopyError,
}: AppErrorScreenProps) {
  const [isCopied, setIsCopied] = React.useState(false)
  const copyResetTimeoutRef = React.useRef<number | null>(null)
  const shouldShowDiagnostics = showDiagnostics ?? showStackTrace

  React.useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current !== null) {
        window.clearTimeout(copyResetTimeoutRef.current)
      }
    }
  }, [])

  const technicalDetails = React.useMemo(() => {
    if (!error) return ""
    const lines = [
      `${labels.routeLabel}: ${pathname}`,
      `${labels.messageLabel}: ${error.message}`,
      error.stack ? `\n${error.stack}` : "",
    ]
    return lines.filter((line) => line.trim().length > 0).join("\n")
  }, [error, labels.messageLabel, labels.routeLabel, pathname])

  const handleCopy = React.useCallback(async () => {
    if (
      !technicalDetails ||
      typeof navigator === "undefined" ||
      typeof navigator.clipboard?.writeText !== "function"
    ) {
      onCopyError?.()
      return
    }

    try {
      await navigator.clipboard.writeText(technicalDetails)
      setIsCopied(true)
      onCopySuccess?.()

      if (copyResetTimeoutRef.current !== null) {
        window.clearTimeout(copyResetTimeoutRef.current)
      }
      copyResetTimeoutRef.current = window.setTimeout(() => {
        setIsCopied(false)
      }, 2000)
    } catch {
      onCopyError?.()
    }
  }, [onCopyError, onCopySuccess, technicalDetails])

  // Phone (below md): no floating card. The screen itself is the page, like the app's other phone screens: the
  // message centred near the top on the app canvas, the two actions as full-width pills pinned to the bottom, and
  // no uppercase eyebrow.
  return (
    <div className='flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-background to-muted/20 px-4 py-10 text-foreground max-md:min-h-[var(--app-height)] max-md:items-stretch max-md:bg-none max-md:bg-[var(--app-canvas)] max-md:px-5 max-md:py-0'>
      <div className='w-full max-w-xl rounded-3xl border border-border/70 bg-card/90 p-6 shadow-[var(--shadow-dialog)] backdrop-blur max-md:flex max-md:max-w-none max-md:flex-col max-md:rounded-none max-md:border-0 max-md:bg-transparent max-md:p-0 max-md:shadow-none max-md:backdrop-blur-none'>
        <div className='flex flex-col gap-6 max-md:flex-1 max-md:gap-5 max-md:pt-[calc(env(safe-area-inset-top)+4.5rem)] max-md:pb-[max(1.75rem,calc(env(safe-area-inset-bottom)+1rem))]'>
          <div className='flex flex-col items-center gap-4 text-center sm:flex-row sm:items-start sm:text-left'>
            <div className='flex size-14 shrink-0 items-center justify-center rounded-2xl bg-destructive/10 text-destructive max-md:size-16 max-md:rounded-[1.25rem]'>
              <CircleAlertIcon className='size-7' />
            </div>

            <div className='space-y-2'>
              <p className='text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground max-md:hidden'>{labels.eyebrow}</p>
              <h1 className='text-2xl font-semibold tracking-tight max-md:text-[1.625rem] max-md:leading-tight max-md:font-extrabold'>{labels.title}</h1>
              <p className='max-w-prose text-sm leading-6 text-muted-foreground max-md:text-[15px] max-md:font-semibold'>{labels.description}</p>
            </div>
          </div>

          <div className='flex flex-col gap-3 sm:flex-row max-md:order-last max-md:mt-auto max-md:gap-2'>
            <Button size='lg' onClick={onReload} className='gap-2 sm:w-auto max-md:h-[3.25rem] max-md:w-full max-md:text-base max-md:font-extrabold'>
              <RefreshCcwIcon className='size-4' />
              {labels.reloadLabel}
            </Button>
            <Button size='lg' variant='outline' onClick={onHome} className='gap-2 sm:w-auto max-md:h-[3.25rem] max-md:w-full max-md:border-0 max-md:bg-transparent max-md:text-base max-md:font-bold max-md:shadow-none'>
              <LayoutDashboardIcon className='size-4' />
              {labels.homeLabel}
            </Button>
          </div>

          {shouldShowDiagnostics ? (
            <div className='rounded-2xl border border-border/70 bg-muted/30 p-4'>
              <p className='text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground'>
                {labels.routeLabel}
              </p>
              <p className='mt-1 break-all text-sm text-foreground'>{pathname}</p>
            </div>
          ) : null}

          {error && shouldShowDiagnostics ? (
            <details className='group max-h-80 overflow-y-auto rounded-2xl border border-border/70 bg-background px-4 pt-0'>
              <summary className='sticky top-0 z-10 flex h-12 cursor-pointer list-none items-center justify-between gap-3 bg-background text-sm font-medium text-foreground'>
                <span>{labels.detailsLabel}</span>
                <Button
                  type='button'
                  size='icon-sm'
                  variant='ghost'
                  className='shrink-0'
                  aria-label={isCopied ? labels.copiedLabel : labels.copyLabel}
                  title={isCopied ? labels.copiedLabel : labels.copyLabel}
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    void handleCopy()
                  }}
                >
                  {isCopied ? <CheckIcon className='size-4' /> : <CopyIcon className='size-4' />}
                </Button>
              </summary>
              <div className='mt-3 space-y-3'>
                <div>
                  <p className='text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground'>
                    {labels.messageLabel}
                  </p>
                  <p className='mt-1 break-words text-sm text-foreground'>{error.message}</p>
                </div>
                {showStackTrace && error.stack ? (
                  <pre className='overflow-auto rounded-xl bg-muted/50 p-3 text-xs leading-5 text-muted-foreground'>
                    {error.stack}
                  </pre>
                ) : null}
              </div>
            </details>
          ) : null}
        </div>
      </div>
    </div>
  )
}
